// Define the structure for an individual activity item
interface ActivityItem {
  icon?: string; // Key for translation or display name
  color?: string;
  displayName: string; // Key for translation or display name
  clickValue: string; // The internal identifier for the activity
  fullWidth?: boolean; // Optional flag for styling
  comingSoon?: boolean; // Optional flag for styling/disabling
  targetLanguages?: string[]; // Optional array of target languages this activity supports (e.g., ['fr', 'en'])
}

// Define the structure for the categories object
interface ActivityCategories {
  activities_pitch: ActivityItem[];
  activities_work: ActivityItem[];
  activities_test_practices: ActivityItem[];
  activities_basic: ActivityItem[];
  activities_custom: ActivityItem[];
  // Add other category keys here if needed, following the pattern:
  // [categoryKey: string]: ActivityItem[];
}

// The actual data structure
const activityCategories: ActivityCategories = {
  // Only shown to learners with a sales pitch saved on their profile (users/{uid}.pitch)
  activities_pitch: [
    { displayName: "activity_pitch_practice", clickValue: "pitch-practice", fullWidth: true },
  ],
  activities_work: [
    // { icon: "route", displayName: "activity_learning_path", clickValue: "learning-path", fullWidth: true, color:"var(--button-action-green)" }, // Follow the learning path
    { icon: "briefcase", displayName: "activity_travail", clickValue: "learning-path", fullWidth: true }, // Generic talk about work
    // { displayName: "Pratique de presentation", clickValue: "presentation", fullWidth: true, comingSoon: true }
  ],
  activities_custom: [
    { displayName: "Answer questions about your documents", clickValue: "custom_questions", fullWidth: true, color:"var(--button-action-orange)" },
    { displayName: "Present this document in your own words", clickValue: "custom_present", fullWidth: true, color:"var(--button-action-orange)" },
    { displayName: "Simulate a conversation with someone based your documents", clickValue: "custom_conversation", fullWidth: true, color:"var(--button-action-orange)" },
    { displayName: "Practice pronunciation of words in your document", clickValue: "custom_pronunciation", fullWidth: true, comingSoon: true, color:"var(--button-action-orange)" }
  ],
  activities_test_practices: [
    { displayName: "Test de niveau (~15min)", clickValue: "placement-test", fullWidth: true },
    { displayName: "Pratique Examen TCF (~15 minutes)", clickValue: "exam-tcf-practice", fullWidth: true, targetLanguages: ['fr'] },
    { displayName: "IELTS General Training Practice (~15 minutes)", clickValue: "exam-ielts-practice", fullWidth: true, targetLanguages: ['en'] },
    { displayName: "TOEFL iBT Speaking Practice (~15 minutes)", clickValue: "exam-toefl-practice", fullWidth: true, targetLanguages: ['en'] },
    { displayName: "CELPIP General Speaking Practice (~15 minutes)", clickValue: "exam-celpip-practice", fullWidth: true, targetLanguages: ['en'] },
    { displayName: "PTE Core Speaking Practice (~15 minutes)", clickValue: "exam-pte-practice", fullWidth: true, targetLanguages: ['en'] },
  ],
  // Everyday situations. clickValues are mirrored in functions/prompt/prompt.js
  // (activityFiles), which also keeps the superseded keys resolvable for old links.
  activities_basic: [
    { displayName: "activity_culture_social_norms", clickValue: "culture_social_norms" },
    { displayName: "activity_greetings_introductions", clickValue: "greetings_introductions" },
    { displayName: "activity_numbers", clickValue: "numbers" },
    { displayName: "activity_people_relationships", clickValue: "people_relationships" },
    { displayName: "activity_food_drinks", clickValue: "food_drinks" },
    { displayName: "activity_places_directions_transport", clickValue: "places_directions_transport" },
    { displayName: "activity_appointments", clickValue: "appointments" },
    { displayName: "activity_weather_small_talk", clickValue: "weather_small_talk" },
    { displayName: "activity_questions_requests_clarification", clickValue: "questions_requests_clarification" },
    { displayName: "activity_shopping_errands", clickValue: "shopping_errands" },
    { displayName: "activity_colors", clickValue: "colors" }
  ],
  // Add other categories here
};


function getActivityDuration(activityName: string): number {
  const DEFAULT_DURATION = 15 * 60 // 15 minutes in seconds
  if (activityName?.startsWith('exam-')) {
    return DEFAULT_DURATION
  }
  return 0
}

// Filter activities based on target language
function filterActivitiesByTargetLanguage(activities: ActivityItem[], targetLanguage: string): ActivityItem[] {
  return activities.filter(activity => {
    // If no targetLanguages specified, show activity for all languages
    if (!activity.targetLanguages || activity.targetLanguages.length === 0) {
      return true;
    }
    // Show activity only if current target language is in the supported languages
    return activity.targetLanguages.includes(targetLanguage);
  });
}

export { getActivityDuration, activityCategories, filterActivitiesByTargetLanguage };
export type { ActivityItem, ActivityCategories }; // Export types if they might be useful in other files